import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  Collapse,
  Divider,
  Button,
  LinearProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import GitHubIcon from "@mui/icons-material/GitHub";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CssIcon from "@mui/icons-material/Css";
import HtmlIcon from "@mui/icons-material/Html";
import JavascriptIcon from "@mui/icons-material/Javascript";
import TerminalIcon from "@mui/icons-material/Terminal";
import StorageIcon from "@mui/icons-material/Storage";
import CodeIcon from "@mui/icons-material/Code";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import type { AnalysisItem, AnalysisApiResponse, AffectedFile, CodeSnippet } from "./bugAnalysis/types";
import {
  confidenceConfig,
  statusConfig,
} from "./bugAnalysis/constants";
import { useGetBugAnalysisQuery, useUpdateDefectAnalysisMutation } from "@/features/bughurd/api";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useRaisePRMutation } from "@/features/CompanyInformation/companyApi";

interface Props {
  bugId: string;
  bugDetailData: any;
}

const confidenceIcons: Record<string, React.ReactNode> = {
  HIGH: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
  MEDIUM: <WarningAmberIcon sx={{ fontSize: 14 }} />,
  LOW: <WarningAmberIcon sx={{ fontSize: 14 }} />,
};

const PAGE_SIZE = 10;

const BugAnalysis = ({ bugId, bugDetailData }: Props) => {
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();

  const projectId = bugDetailData?.project?._id || bugDetailData?.project;

  const [shouldPoll, setShouldPoll] = useState(true);
  const [page, setPage] = useState(1);
  const [allItems, setAllItems] = useState<AnalysisItem[]>([]);

  const { data: apiData, isLoading, isFetching, refetch } = useGetBugAnalysisQuery(
    { bugId, projectId, page, size: PAGE_SIZE },
    {
      skip: !bugId || !projectId,
      pollingInterval: shouldPoll ? 10000 : 0,
    },
  );

  const response: AnalysisApiResponse | null = apiData?.data?.localData ?? apiData?.data ?? null;

  useEffect(() => {
    if (response?.overallStatus === "done" || response?.overallStatus === "failed") {
      setShouldPoll(false);
    }
  }, [response?.overallStatus]);

  // Accumulate items across pages
  useEffect(() => {
    if (!response?.items) return;
    if (page === 1) {
      setAllItems(response.items);
    } else {
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((i) => i._id));
        const newItems = response.items.filter((i) => !existingIds.has(i._id));
        return [...prev, ...newItems];
      });
    }
  }, [response?.items, page]);

  const items = allItems;
  const totalCount = response?.totalRepositories ?? 0;
  const hasMore = items.length < totalCount;

  const [expandedPanels, setExpandedPanels] = useState<Record<number, boolean>>({});
  const hasAutoExpanded = useRef(false);

  useEffect(() => {
    if (hasAutoExpanded.current || items.length === 0) return;
    hasAutoExpanded.current = true;
    const firstWithAnalysis = items.findIndex(
      (item) =>
        item.individualStatus === "completed" &&
        item.analysis &&
        !item.analysis.error,
    );
    if (firstWithAnalysis >= 0) setExpandedPanels({ [firstWithAnalysis]: true });
    else setExpandedPanels({ 0: true });
  }, [items]);

  const togglePanel = (index: number) => {
    setExpandedPanels((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const expandAll = () => {
    const all: Record<number, boolean> = {};
    items.forEach((_, i) => (all[i] = true));
    setExpandedPanels(all);
  };

  const collapseAll = () => {
    setExpandedPanels({});
  };

  const [raisePR] = useRaisePRMutation();
  const [updateDefectAnalysis] = useUpdateDefectAnalysisMutation();
  const [raisingPRIds, setRaisingPRIds] = useState<Set<string>>(new Set());
  const [raisingAllPRs, setRaisingAllPRs] = useState(false);

  const buildPRPayload = (item: AnalysisItem) => {
    const repoName = item.repositoryUrl
      .replace(/\.git$/, "")
      .split("/")
      .pop() || "";

    const files = (item.analysis?.codeSnippets || []).map((snippet) => ({
      filePath: snippet.filePath,
      fileContent: snippet.suggested,
    }));

    const commitMessage = bugDetailData?.title
      ? `fix: ${bugDetailData.title}`
      : "fix: resolve detected issues";

    return {
      repoName,
      sourceBranch: item.branch,
      targetBranch: item.branch,
      files,
      commitMessage,
    };
  };

  const handleRaisePR = async (item: AnalysisItem) => {
    if (item.isPrRaised || raisingPRIds.has(item._id)) return;

    const payload = buildPRPayload(item);
    if (!payload.files.length) {
      toast.error(t("No code changes available to raise a PR"));
      return;
    }

    setRaisingPRIds((prev) => new Set(prev).add(item._id));
    try {
      const res: any = await raisePR(payload).unwrap();
      const prUrl =
        res?.localData?.pullRequest?.url ||
        res?.data?.localData?.pullRequest?.url ||
        res?.pullRequest?.url ||
        null;

      await updateDefectAnalysis({
        analysisId: item._id,
        isPrRaised: true,
        prUrl,
      }).unwrap();

      toast.success(t("PR raised successfully for") + ` ${payload.repoName}`);
      refetch();
    } catch (err: any) {
      toast.error(
        err?.data?.message || t("Failed to raise PR for") + ` ${payload.repoName}`,
      );
    } finally {
      setRaisingPRIds((prev) => {
        const next = new Set(prev);
        next.delete(item._id);
        return next;
      });
    }
  };

  const handleRaiseAllPRs = async () => {
    const eligibleItems = items.filter(
      (item) =>
        item.individualStatus === "completed" &&
        item.analysis &&
        !item.analysis.error &&
        item.analysis.summary &&
        !item.isPrRaised &&
        (item.analysis.codeSnippets?.length ?? 0) > 0,
    );

    if (!eligibleItems.length) {
      toast.error(t("No eligible repositories to raise PRs"));
      return;
    }

    setRaisingAllPRs(true);
    let successCount = 0;
    let failCount = 0;

    for (const item of eligibleItems) {
      const payload = buildPRPayload(item);
      setRaisingPRIds((prev) => new Set(prev).add(item._id));
      try {
        const res: any = await raisePR(payload).unwrap();
        const prUrl =
          res?.localData?.pullRequest?.url ||
          res?.data?.localData?.pullRequest?.url ||
          res?.pullRequest?.url ||
          null;

        await updateDefectAnalysis({
          analysisId: item._id,
          isPrRaised: true,
          prUrl,
        }).unwrap();

        successCount++;
      } catch {
        failCount++;
      } finally {
        setRaisingPRIds((prev) => {
          const next = new Set(prev);
          next.delete(item._id);
          return next;
        });
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} PR(s) raised successfully`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} PR(s) failed to raise`);
    }
    setRaisingAllPRs(false);
    refetch();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const applicationsPath = `/defect-tracker/projects/${projectId}/applications`;

  if (!items.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 5,
          px: 3,
          textAlign: "center",
          borderRadius: "14px",
          border: (theme) =>
            `1.5px dashed ${theme.palette.divider}`,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.primary.main}0A 0%, ${theme.palette.primary.main}06 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main}06 0%, ${theme.palette.primary.main}04 100%)`,
        }}
      >
        {/* Icon badge */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "14px",
            mx: "auto",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? `${theme.palette.primary.main}20`
                : `${theme.palette.primary.main}14`,
          }}
        >
          <AutoFixHighIcon
            sx={{
              fontSize: 28,
              color: "primary.main",
            }}
          />
        </Box>

        <Typography
          variant="body1"
          fontWeight={800}
          fontSize="14px"
          color="text.primary"
          sx={{ mb: 0.5 }}
        >
          {t("No analysis available yet")}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          fontSize="12px"
          sx={{ display: "block", mb: 1, maxWidth: 380, mx: "auto", lineHeight: 1.7 }}
        >
          {t(
            "To run bug analysis, your project needs applications with linked repositories.",
          )}
        </Typography>

        {/* Steps */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mb: 2.5,
            mt: 2,
          }}
        >
          {[
            { step: "1", label: t("Add Application"), icon: <StorageIcon sx={{ fontSize: 16 }} /> },
            { step: "2", label: t("Link Repository"), icon: <GitHubIcon sx={{ fontSize: 16 }} /> },
            { step: "3", label: t("Run Analysis"), icon: <AutoFixHighIcon sx={{ fontSize: 16 }} /> },
          ].map((s) => (
            <Box
              key={s.step}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? `${theme.palette.primary.main}18`
                      : `${theme.palette.primary.main}12`,
                  color: "primary.main",
                }}
              >
                {s.icon}
              </Box>
              <Typography fontSize="10px" fontWeight={700} color="text.secondary">
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          size="small"
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<StorageIcon sx={{ fontSize: 15 }} />}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
          onClick={() => navigate(applicationsPath)}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 700,
            borderRadius: "8px",
            px: 3,
            py: 0.8,
            boxShadow: "none",
          }}
        >
          {t("Go to Applications")}
        </Button>
      </Paper>
    );
  }

  const allExpanded = items.every((_, i) => expandedPanels[i]);
  const isOverallInProgress =
    response?.overallStatus === "in_progress" || response?.overallStatus === "pending";
  const completedCount = response?.completedRepositories ?? 0;

  const hasEligiblePRItems = items.some(
    (item) =>
      item.individualStatus === "completed" &&
      item.analysis &&
      !item.analysis.error &&
      item.analysis.summary,
  );

  // Dashboard stats
  const uniqueApps = new Set(items.map((i) => i.applicationName).filter(Boolean)).size;
  const totalAffectedFiles = items.reduce(
    (sum, i) => sum + (i.analysis?.affectedFiles?.length ?? 0),
    0,
  );
  const totalCodeChanges = items.reduce(
    (sum, i) => sum + (i.analysis?.codeSnippets?.length ?? 0),
    0,
  );
  const prsRaised = items.filter((i) => i.isPrRaised).length;

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(139,92,246,0.2), rgba(99,102,241,0.2))"
                  : "linear-gradient(135deg, #ede9fe, #e0e7ff)",
            }}
          >
            <AutoFixHighIcon
              sx={{
                fontSize: 16,
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
              }}
            />
          </Box>
          <Typography variant="body2" fontWeight={700} fontSize="13px">
            {t("Bug Analysis")}
          </Typography>
          <Chip
            label={`${completedCount}/${totalCount} ${t("repos")}`}
            size="small"
            sx={{
              fontSize: "10px",
              height: 20,
              fontWeight: 700,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(139,92,246,0.15)"
                  : "#ede9fe",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
            }}
          />
          {isOverallInProgress && (
            <Chip
              label={t("In Progress")}
              size="small"
              sx={{
                fontSize: "9px",
                height: 18,
                fontWeight: 700,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(59,130,246,0.12)"
                    : "#eff6ff",
                color: "#3b82f6",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.6 },
                },
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title={t("Refresh")}>
            <IconButton
              size="small"
              onClick={() => refetch()}
              sx={{ p: 0.5 }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  color: "text.secondary",
                  ...(isFetching && {
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }),
                }}
              />
            </IconButton>
          </Tooltip>
          {/* Raise All PRs — hidden for now */}
          <Button
            size="small"
            onClick={allExpanded ? collapseAll : expandAll}
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 600,
              color: "text.secondary",
              minWidth: "auto",
              px: 1,
            }}
          >
            {allExpanded ? t("Collapse All") : t("Expand All")}
          </Button>
        </Box>
      </Box>

      {/* Overall progress bar when still analyzing */}
      {isOverallInProgress && (
        <LinearProgress
          variant="determinate"
          value={totalCount > 0 ? (completedCount / totalCount) * 100 : 0}
          sx={{
            mb: 1.5,
            height: 3,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "#f3f4f6",
            "& .MuiLinearProgress-bar": { bgcolor: "#7c3aed" },
          }}
        />
      )}

      {/* Dashboard Summary */}
      <Box
        display="flex"
        alignItems="stretch"
        gap={0}
        flexWrap="wrap"
        sx={{
          mb: 2,
          borderRadius: "10px",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "#f9fafb",
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#e5e7eb"}`,
          overflow: "hidden",
        }}
      >
        <DashboardStat
          icon={<GitHubIcon sx={{ fontSize: 16 }} />}
          value={`${completedCount}/${totalCount}`}
          label={t("Repositories")}
          color="#7c3aed"
        />
        {uniqueApps > 0 && (
          <DashboardStat
            icon={<StorageIcon sx={{ fontSize: 16 }} />}
            value={String(uniqueApps)}
            label={t("Applications")}
            color="#6366f1"
          />
        )}
        <DashboardStat
          icon={<FolderOpenIcon sx={{ fontSize: 16 }} />}
          value={String(totalAffectedFiles)}
          label={t("Affected Files")}
          color="#d97706"
          highlight={totalAffectedFiles === 0}
        />
        <DashboardStat
          icon={<CodeIcon sx={{ fontSize: 16 }} />}
          value={totalCodeChanges > 0 ? String(totalCodeChanges) : t("None")}
          label={t("Code Changes")}
          color={totalCodeChanges > 0 ? "#059669" : "#9ca3af"}
          highlight={totalCodeChanges === 0}
        />
        {prsRaised > 0 && (
          <DashboardStat
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            value={String(prsRaised)}
            label={t("PRs Raised")}
            color="#10b981"
          />
        )}
      </Box>

      {/* Accordion List */}
      <Box display="flex" flexDirection="column" gap={1.5}>
        {items.map((item, index) => (
          <RepoAccordion
            key={item._id}
            item={item}
            expanded={!!expandedPanels[index]}
            onToggle={() => togglePanel(index)}
            onRaisePR={() => handleRaisePR(item)}
            isRaisingPR={raisingPRIds.has(item._id)}
          />
        ))}
      </Box>

      {/* Load More */}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            size="small"
            disabled={isFetching}
            onClick={() => setPage((prev) => prev + 1)}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(139,92,246,0.3)"
                  : "#e0e7ff",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
              "&:hover": {
                borderColor: "#7c3aed",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(139,92,246,0.08)"
                    : "#f5f3ff",
              },
            }}
          >
            {isFetching ? (
              <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />
            ) : null}
            {t("Load More")} ({items.length}/{totalCount})
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─── Repo Accordion ─────────────────────────────────────────────────────────

function RepoAccordion({
  item,
  expanded,
  onToggle,
  onRaisePR,
  isRaisingPR,
}: {
  item: AnalysisItem;
  expanded: boolean;
  onToggle: () => void;
  onRaisePR: () => void;
  isRaisingPR: boolean;
}) {
  const { t } = useTranslation(["bugherd"]);
  const analysis = item.analysis;
  const hasAnalysis = analysis && !analysis.error && analysis.summary;
  const hasError = analysis?.error || item.error;
  const isPending = item.individualStatus === "pending";
  const isInProgress = item.individualStatus === "in_progress";
  const conf = hasAnalysis && analysis.confidenceLevel
    ? confidenceConfig[analysis.confidenceLevel]
    : null;
  const stConf = statusConfig[item.individualStatus] || statusConfig.pending;

  // Extract repo name from URL
  const repoName = item.repositoryUrl
    .replace(/\.git$/, "")
    .split("/")
    .pop() || item.repositoryUrl;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t("Copied to clipboard"));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "10px",
        border: (theme) =>
          `1px solid ${
            expanded && conf
              ? theme.palette.mode === "dark"
                ? conf.darkBorderColor
                : conf.borderColor
              : theme.palette.divider
          }`,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        opacity: isPending ? 0.6 : 1,
        ...(expanded && conf && {
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? `0 0 0 1px ${conf.darkBorderColor}`
              : `0 1px 8px ${conf.color}10`,
        }),
      }}
    >
      {/* ── Header ── */}
      <Box
        onClick={onToggle}
        sx={{
          px: 2,
          py: 1.2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          transition: "background 0.15s",
          "&:hover": {
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.01)",
          },
        }}
      >
        {/* Status dot */}
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: stConf.color,
            flexShrink: 0,
            boxShadow: `0 0 5px ${stConf.color}40`,
            ...(isInProgress && {
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.3 },
              },
            }),
          }}
        />
        <GitHubIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        <Typography
          variant="body2"
          fontWeight={700}
          fontSize="12px"
          noWrap
          sx={{ minWidth: 0 }}
        >
          {repoName}
        </Typography>
        {item.applicationName && (
          <Chip
            label={`App: ${item.applicationName}`}
            size="small"
            sx={{
              fontSize: "9px",
              height: 18,
              fontWeight: 600,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(139,92,246,0.1)"
                  : "#ede9fe",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
            }}
          />
        )}
        <Chip
          label={`Branch: ${item.branch}`}
          size="small"
          sx={{
            fontFamily: "monospace",
            fontSize: "9.5px",
            height: 18,
            fontWeight: 600,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "#f3f4f6",
            color: "text.secondary",
          }}
        />
        {/* Status / Confidence chip */}
        {hasAnalysis && conf ? (
          <Chip
            icon={
              <Box sx={{ display: "flex", color: conf.color, ml: 0.5 }}>
                {confidenceIcons[analysis.confidenceLevel!]}
              </Box>
            }
            label={conf.label}
            size="small"
            sx={{
              fontSize: "9px",
              height: 20,
              fontWeight: 700,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? conf.darkBg : conf.bg,
              color: conf.color,
              border: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? conf.darkBorderColor : conf.borderColor}`,
              "& .MuiChip-icon": { mr: -0.3 },
            }}
          />
        ) : (
          <Chip
            label={stConf.label}
            size="small"
            sx={{
              fontSize: "9px",
              height: 20,
              fontWeight: 700,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? stConf.darkBg : stConf.bg,
              color: stConf.color,
              ...(isInProgress && {
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.6 },
                },
              }),
            }}
          />
        )}
        <Box flex={1} />
        {/* Right-side quick info chips */}
        {item.individualStatus === "completed" && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ display: { xs: "none", sm: "flex" } }}>
            {hasError ? (
              <Chip
                icon={<ErrorOutlineIcon sx={{ fontSize: 11 }} />}
                label={t("No changes")}
                size="small"
                sx={{
                  fontSize: "9px",
                  height: 18,
                  fontWeight: 600,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(217,119,6,0.08)"
                      : "#fef3c7",
                  color: "#d97706",
                  "& .MuiChip-icon": { color: "#d97706", ml: 0.3 },
                }}
              />
            ) : hasAnalysis ? (
              <>
                {(analysis.affectedFiles?.length ?? 0) > 0 && (
                  <Chip
                    icon={<FolderOpenIcon sx={{ fontSize: 11 }} />}
                    label={`${analysis.affectedFiles!.length} ${t("affected")}`}
                    size="small"
                    sx={{
                      fontSize: "9px",
                      height: 18,
                      fontWeight: 600,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(217,119,6,0.1)"
                          : "#fef3c7",
                      color: "#d97706",
                      "& .MuiChip-icon": { color: "#d97706", ml: 0.3 },
                    }}
                  />
                )}
                {(analysis.codeSnippets?.length ?? 0) > 0 ? (
                  <Chip
                    icon={<CodeIcon sx={{ fontSize: 11 }} />}
                    label={`${analysis.codeSnippets!.length} ${t("changes")}`}
                    size="small"
                    sx={{
                      fontSize: "9px",
                      height: 18,
                      fontWeight: 600,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(16,185,129,0.1)"
                          : "#ecfdf5",
                      color: "#059669",
                      "& .MuiChip-icon": { color: "#059669", ml: 0.3 },
                    }}
                  />
                ) : (
                  <Chip
                    label={t("No changes")}
                    size="small"
                    sx={{
                      fontSize: "9px",
                      height: 18,
                      fontWeight: 600,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "#f3f4f6",
                      color: "text.disabled",
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.disabled"
                  fontSize="9px"
                  fontWeight={500}
                >
                  {analysis.filesAnalyzed} {t("scanned")}
                </Typography>
              </>
            ) : (
              <Chip
                label={t("No changes")}
                size="small"
                sx={{
                  fontSize: "9px",
                  height: 18,
                  fontWeight: 600,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "#f3f4f6",
                  color: "text.disabled",
                }}
              />
            )}
          </Box>
        )}
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: "text.secondary",
            transition: "transform 0.25s",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {expanded && hasAnalysis && conf && (
        <LinearProgress
          variant="determinate"
          value={conf.percent}
          sx={{
            height: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "#f3f4f6",
            "& .MuiLinearProgress-bar": { bgcolor: conf.color },
          }}
        />
      )}

      {expanded && isInProgress && (
        <LinearProgress
          sx={{
            height: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "#f3f4f6",
            "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" },
          }}
        />
      )}

      {/* ── Content ── */}
      <Collapse in={expanded}>
        <Divider />

        {/* ── In Progress state ── */}
        {(isInProgress || isPending) && (
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {isInProgress ? (
              <CircularProgress size={24} sx={{ color: "#3b82f6" }} />
            ) : (
              <HourglassEmptyIcon sx={{ fontSize: 24, color: "text.disabled" }} />
            )}
            <Typography
              variant="body2"
              fontWeight={600}
              fontSize="12px"
              color={isInProgress ? "#3b82f6" : "text.disabled"}
            >
              {isInProgress
                ? t("Analyzing repository...")
                : t("Waiting in queue")}
            </Typography>
            <Typography
              variant="caption"
              fontSize="11px"
              color="text.disabled"
              textAlign="center"
              sx={{ maxWidth: 320 }}
            >
              {isInProgress
                ? t("AI is scanning the codebase for potential root causes and fixes. This may take a few moments.")
                : t("This repository is queued for analysis and will start soon.")}
            </Typography>
          </Box>
        )}

        {/* ── Error state ── */}
        {item.individualStatus === "completed" && hasError && (
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.2,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 18, color: "#d97706", mt: 0.1, flexShrink: 0 }}
            />
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                fontSize="12px"
                color="#d97706"
                sx={{ mb: 0.5 }}
              >
                {t("Analysis could not be completed")}
              </Typography>
              <Typography
                variant="body2"
                fontSize="11.5px"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                {analysis?.error || item.error}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                mt={1.5}
                sx={{
                  px: 1.2,
                  py: 0.6,
                  borderRadius: "6px",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "#f9fafb",
                }}
              >
                <MetaItem
                  icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 11 }} />}
                  label={String(analysis?.filesAnalyzed ?? 0)}
                  sub={t("files scanned")}
                />
                <MetaItem
                  icon={<CodeIcon sx={{ fontSize: 11 }} />}
                  label={item.branch}
                  mono
                />
                {item.analyzedAt && (
                  <MetaItem
                    icon={<AccessTimeIcon sx={{ fontSize: 11 }} />}
                    label={new Date(item.analyzedAt).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Success state — full analysis ── */}
        {hasAnalysis && (
          <Box sx={{ p: 2, pt: 1.5 }}>
            {/* ─ Meta bar ─ */}
            <Box
              display="flex"
              alignItems="center"
              gap={2.5}
              flexWrap="wrap"
              sx={{
                px: 1.5,
                py: 0.8,
                mb: 1.5,
                borderRadius: "6px",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "#f9fafb",
              }}
            >
              <MetaItem
                icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 11 }} />}
                label={String(analysis.filesAnalyzed)}
                sub={t("files")}
              />
              <MetaItem
                icon={<CodeIcon sx={{ fontSize: 11 }} />}
                label={item.branch}
                mono
              />
              {item.analyzedAt && (
                <MetaItem
                  icon={<AccessTimeIcon sx={{ fontSize: 11 }} />}
                  label={new Date(item.analyzedAt).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                />
              )}
              <Box flex={1} />
              {item.isPrRaised && item.prUrl ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                  onClick={() => window.open(item.prUrl!, "_blank")}
                  sx={{
                    textTransform: "none",
                    fontSize: "10px",
                    fontWeight: 600,
                    borderRadius: "5px",
                    px: 1.5,
                    py: 0.3,
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(16,185,129,0.3)"
                        : "#a7f3d0",
                    color: "#059669",
                    "&:hover": {
                      borderColor: "#059669",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(16,185,129,0.08)"
                          : "#ecfdf5",
                    },
                  }}
                >
                  {t("View PR")}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isRaisingPR || item.isPrRaised}
                  startIcon={
                    isRaisingPR ? (
                      <CircularProgress size={10} sx={{ color: "inherit" }} />
                    ) : (
                      <GitHubIcon sx={{ fontSize: 13 }} />
                    )
                  }
                  onClick={onRaisePR}
                  sx={{
                    textTransform: "none",
                    fontSize: "10px",
                    fontWeight: 600,
                    borderRadius: "5px",
                    px: 1.5,
                    py: 0.3,
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(139,92,246,0.3)"
                        : "#e0e7ff",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
                    "&:hover": {
                      borderColor: "#7c3aed",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(139,92,246,0.08)"
                          : "#f5f3ff",
                    },
                  }}
                >
                  {isRaisingPR ? t("Raising...") : t("Raise PR")}
                </Button>
              )}
            </Box>

            {/* ─ Summary ─ */}
            <Typography
              variant="body2"
              fontSize="12px"
              color="text.secondary"
              sx={{ lineHeight: 1.75, mb: 2 }}
            >
              {analysis.summary}
            </Typography>

            {/* ─ Root Cause + Suggested Fix — tabbed card ─ */}
            {analysis.rootCause && analysis.suggestedFix && (
              <InsightTabs
                rootCause={analysis.rootCause}
                suggestedFix={analysis.suggestedFix}
                onCopy={copyToClipboard}
              />
            )}

            {/* ─ Code Changes ─ */}
            {(analysis.codeSnippets?.length ?? 0) > 0 && (
              <InlineCodeChanges snippets={analysis.codeSnippets!} />
            )}

            {/* ─ Affected Files ─ */}
            {(analysis.affectedFiles?.length ?? 0) > 0 && (
              <InlineAffectedFiles files={analysis.affectedFiles!} />
            )}

            {/* ─ Additional Notes ─ */}
            {analysis.additionalNotes && (
              <Box
                display="flex"
                alignItems="flex-start"
                gap={0.8}
                sx={{
                  mt: 0.5,
                  px: 1.2,
                  py: 0.8,
                  borderRadius: "6px",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(99,102,241,0.04)"
                      : "#eef2ff",
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(99,102,241,0.1)" : "#e0e7ff"}`,
                }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 13, color: "#6366f1", mt: 0.15, flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  fontSize="11px"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {analysis.additionalNotes}
                </Typography>
              </Box>
            )}

          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// ─── Inline Code Changes ────────────────────────────────────────────────────

/** Build a unified diff: removed lines (red) then added lines (green) */
function buildUnifiedDiff(original: string, suggested: string) {
  const origLines = original.split("\n");
  const sugLines = suggested.split("\n");

  // Simple diff: find common prefix/suffix, mark middle as changed
  let prefixLen = 0;
  while (
    prefixLen < origLines.length &&
    prefixLen < sugLines.length &&
    origLines[prefixLen] === sugLines[prefixLen]
  ) {
    prefixLen++;
  }

  let suffixLen = 0;
  while (
    suffixLen < origLines.length - prefixLen &&
    suffixLen < sugLines.length - prefixLen &&
    origLines[origLines.length - 1 - suffixLen] ===
      sugLines[sugLines.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const lines: { text: string; type: "context" | "removed" | "added" }[] = [];

  // Common prefix
  for (let i = 0; i < prefixLen; i++) {
    lines.push({ text: origLines[i], type: "context" });
  }
  // Removed lines
  for (let i = prefixLen; i < origLines.length - suffixLen; i++) {
    lines.push({ text: origLines[i], type: "removed" });
  }
  // Added lines
  for (let i = prefixLen; i < sugLines.length - suffixLen; i++) {
    lines.push({ text: sugLines[i], type: "added" });
  }
  // Common suffix
  for (let i = origLines.length - suffixLen; i < origLines.length; i++) {
    lines.push({ text: origLines[i], type: "context" });
  }

  return lines;
}

type DiffViewMode = "unified" | "original" | "suggested";

function InlineCodeChanges({ snippets }: { snippets: CodeSnippet[] }) {
  const { t } = useTranslation(["bugherd"]);
  const [expanded, setExpanded] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [viewMode, setViewMode] = useState<DiffViewMode>("unified");

  const codeFont = "'JetBrains Mono', 'Fira Code', monospace";
  const activeSnippet = snippets[activeIdx];
  const diffLines = buildUnifiedDiff(
    activeSnippet.original,
    activeSnippet.suggested,
  );

  const removedCount = diffLines.filter((l) => l.type === "removed").length;
  const addedCount = diffLines.filter((l) => l.type === "added").length;

  const copyCode = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t("Copied to clipboard"));
  };

  const getFileName = (path: string) => path.split("/").pop() || path;
  const getFileDir = (path: string) => {
    const parts = path.split("/");
    return parts.length > 1 ? parts.slice(0, -1).join("/") : "";
  };

  const langColors: Record<string, string> = {
    typescript: "#3178c6",
    javascript: "#f7df1e",
    python: "#3776ab",
    java: "#ed8b00",
    css: "#264de4",
    html: "#e34c26",
    json: "#292929",
    go: "#00add8",
    rust: "#dea584",
  };

  const FileIconBadge = ({ icon, bgColor }: { icon: React.ReactNode; bgColor: string }) => (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "5px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? `${bgColor}20` : `${bgColor}18`,
      }}
    >
      {icon}
    </Box>
  );

  const getFileIcon = (language: string, filePath: string) => {
    const lang = language.toLowerCase();
    const ext = filePath.split(".").pop()?.toLowerCase() || "";
    const iconSx = { fontSize: 15 };

    if (lang === "typescript" || ext === "ts" || ext === "tsx")
      return <FileIconBadge bgColor="#3178c6" icon={<CodeIcon sx={{ ...iconSx, color: "#3178c6" }} />} />;
    if (lang === "javascript" || ext === "js" || ext === "jsx")
      return <FileIconBadge bgColor="#b8960c" icon={<JavascriptIcon sx={{ ...iconSx, color: "#b8960c" }} />} />;
    if (lang === "css" || ext === "css" || ext === "scss" || ext === "less")
      return <FileIconBadge bgColor="#264de4" icon={<CssIcon sx={{ ...iconSx, color: "#264de4" }} />} />;
    if (lang === "html" || ext === "html" || ext === "htm")
      return <FileIconBadge bgColor="#e34c26" icon={<HtmlIcon sx={{ ...iconSx, color: "#e34c26" }} />} />;
    if (lang === "json" || ext === "json")
      return <FileIconBadge bgColor="#4b5563" icon={<DataObjectIcon sx={{ ...iconSx, color: "#4b5563" }} />} />;
    if (lang === "python" || ext === "py")
      return <FileIconBadge bgColor="#3776ab" icon={<TerminalIcon sx={{ ...iconSx, color: "#3776ab" }} />} />;
    if (lang === "java" || ext === "java")
      return <FileIconBadge bgColor="#ed8b00" icon={<StorageIcon sx={{ ...iconSx, color: "#ed8b00" }} />} />;
    if (lang === "go" || ext === "go")
      return <FileIconBadge bgColor="#00add8" icon={<CodeIcon sx={{ ...iconSx, color: "#00add8" }} />} />;
    if (lang === "rust" || ext === "rs")
      return <FileIconBadge bgColor="#dea584" icon={<CodeIcon sx={{ ...iconSx, color: "#dea584" }} />} />;
    if (ext === "sql")
      return <FileIconBadge bgColor="#336791" icon={<StorageIcon sx={{ ...iconSx, color: "#336791" }} />} />;
    if (ext === "md" || ext === "mdx")
      return <FileIconBadge bgColor="#4b5563" icon={<DescriptionOutlinedIcon sx={{ ...iconSx, color: "#4b5563" }} />} />;
    const fallbackColor = langColors[lang] || "#4b5563";
    return <FileIconBadge bgColor={fallbackColor} icon={<InsertDriveFileOutlinedIcon sx={{ ...iconSx, color: fallbackColor }} />} />;
  };

  // Line bg colors per type
  const lineBg = {
    context: { light: "transparent", dark: "transparent" },
    removed: { light: "#fef2f2", dark: "rgba(239,68,68,0.08)" },
    added: { light: "#ecfdf5", dark: "rgba(16,185,129,0.08)" },
  };
  const lineGutterBg = {
    context: { light: "#f6f6f7", dark: "#16161e" },
    removed: { light: "#fee2e2", dark: "rgba(239,68,68,0.15)" },
    added: { light: "#d1fae5", dark: "rgba(16,185,129,0.15)" },
  };
  const prefixColor = {
    context: "text.disabled",
    removed: "#dc2626",
    added: "#059669",
  };
  const prefixChar = { context: " ", removed: "-", added: "+" };

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: "8px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      }}
    >
      {/* ── Title bar ── */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 1.5,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          cursor: "pointer",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#1e1e2e" : "#f3f3f3",
          borderBottom: (theme) =>
            expanded ? `1px solid ${theme.palette.divider}` : "none",
        }}
      >
        <CodeIcon sx={{ fontSize: 13, color: "#6366f1" }} />
        <Typography fontSize="11px" fontWeight={700} color="text.primary">
          {t("Code Changes")}
        </Typography>
        <Chip
          label={`${snippets.length} ${snippets.length === 1 ? "file" : "files"}`}
          size="small"
          sx={{
            fontSize: "9px",
            height: 16,
            fontWeight: 700,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(99,102,241,0.15)"
                : "#e0e7ff",
            color: "#6366f1",
          }}
        />
        <Box flex={1} />
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: "text.secondary",
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "200px 1fr",
            minHeight: 250,
          }}
        >
          {/* ── Left: File Explorer Sidebar ── */}
          <Box
            sx={{
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#181825" : "#f7f7f8",
              overflowY: "auto",
            }}
          >
            <Typography
              fontSize="9px"
              fontWeight={700}
              letterSpacing={0.8}
              textTransform="uppercase"
              color="text.disabled"
              sx={{ px: 1.2, pt: 1.2, pb: 0.6 }}
            >
              {t("Changed Files")}
            </Typography>

            {snippets.map((snippet, idx) => {
              const isActive = idx === activeIdx;
              return (
                <Box
                  key={idx}
                  onClick={() => setActiveIdx(idx)}
                  sx={{
                    px: 1.2,
                    py: 0.8,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    bgcolor: isActive
                      ? (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(99,102,241,0.12)"
                            : "#e8e8ff"
                      : "transparent",
                    borderLeft: isActive
                      ? "2px solid #6366f1"
                      : "2px solid transparent",
                    transition: "all 0.12s",
                    "&:hover": {
                      bgcolor: (theme) =>
                        isActive
                          ? theme.palette.mode === "dark"
                            ? "rgba(99,102,241,0.12)"
                            : "#e8e8ff"
                          : theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "#ededf0",
                    },
                  }}
                >
                  {getFileIcon(snippet.language, snippet.filePath)}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      fontSize="11.5px"
                      fontWeight={isActive ? 700 : 600}
                      fontFamily={codeFont}
                      color={isActive ? "primary.main" : "text.primary"}
                      noWrap
                    >
                      {getFileName(snippet.filePath)}
                    </Typography>
                    {getFileDir(snippet.filePath) && (
                      <Typography
                        fontSize="9.5px"
                        color="text.secondary"
                        fontFamily={codeFont}
                        noWrap
                      >
                        {getFileDir(snippet.filePath)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* ── Right: Unified Diff Panel ── */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#1e1e2e" : "#ffffff",
            }}
          >
            {/* Tab bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.divider}`,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#1a1a2a" : "#f9f9fa",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.6,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  borderBottom: "2px solid #6366f1",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#1e1e2e" : "#ffffff",
                }}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: 12, color: "text.secondary" }}
                />
                <Typography
                  fontSize="10.5px"
                  fontWeight={600}
                  fontFamily={codeFont}
                  color="text.primary"
                  noWrap
                >
                  {getFileName(activeSnippet.filePath)}
                </Typography>
                <Chip
                  label={activeSnippet.language}
                  size="small"
                  sx={{
                    fontSize: "8px",
                    height: 14,
                    fontWeight: 700,
                    fontFamily: "monospace",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.06)"
                        : "#ebebeb",
                  }}
                />
              </Box>

              <Box flex={1} />

              {/* View mode toggle */}
              <Box
                sx={{
                  display: "flex",
                  mr: 1,
                  borderRadius: "5px",
                  overflow: "hidden",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                {(
                  [
                    { key: "unified", label: t("Unified"), color: "#6366f1" },
                    { key: "original", label: t("Original"), color: "#dc2626" },
                    { key: "suggested", label: t("Suggested"), color: "#059669" },
                  ] as const
                ).map((opt, i) => (
                  <Box
                    key={opt.key}
                    onClick={() => setViewMode(opt.key)}
                    sx={{
                      px: 1,
                      py: 0.35,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "9px",
                      fontWeight: 700,
                      color:
                        viewMode === opt.key ? opt.color : "text.disabled",
                      bgcolor:
                        viewMode === opt.key
                          ? (theme) =>
                              theme.palette.mode === "dark"
                                ? `${opt.color}18`
                                : `${opt.color}0D`
                          : "transparent",
                      borderLeft:
                        i > 0
                          ? (theme) =>
                              `1px solid ${theme.palette.divider}`
                          : "none",
                      transition: "all 0.15s",
                      "&:hover": {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? `${opt.color}12`
                            : `${opt.color}08`,
                      },
                    }}
                  >
                    {opt.label}
                  </Box>
                ))}
              </Box>

              {/* Diff stats */}
              <Box display="flex" alignItems="center" gap={0.8} mr={0.5}>
                <Typography fontSize="9.5px" fontWeight={700} color="#059669">
                  +{addedCount}
                </Typography>
                <Typography fontSize="9.5px" fontWeight={700} color="#dc2626">
                  -{removedCount}
                </Typography>
              </Box>

              <Tooltip title={t("Copy suggested code")}>
                <IconButton
                  size="small"
                  onClick={() => copyCode(activeSnippet.suggested)}
                  sx={{ mr: 0.5, p: 0.3 }}
                >
                  <ContentCopyIcon
                    sx={{ fontSize: 13, color: "text.disabled" }}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Explanation bar */}
            <Box
              sx={{
                px: 1.5,
                py: 0.8,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(139,92,246,0.05)"
                    : "#faf5ff",
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.6,
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 12,
                  color: "#7c3aed",
                  mt: 0.15,
                  flexShrink: 0,
                }}
              />
              <Typography
                fontSize="10.5px"
                color="text.secondary"
                sx={{ lineHeight: 1.55 }}
              >
                {activeSnippet.explanation}
              </Typography>
            </Box>

            {/* ── Code view area ── */}
            {viewMode === "unified" ? (
              /* Unified diff — red/green in one view */
              <Box
                sx={{
                  flex: 1,
                  maxHeight: 340,
                  overflow: "auto",
                }}
              >
                {diffLines.map((line, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      minHeight: "1.85em",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? lineBg[line.type].dark
                          : lineBg[line.type].light,
                      "&:hover": {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    {/* Line number gutter */}
                    <Box
                      sx={{
                        width: 44,
                        flexShrink: 0,
                        textAlign: "right",
                        pr: 1.2,
                        pl: 0.8,
                        fontSize: "9.5px",
                        fontFamily: codeFont,
                        lineHeight: "1.85em",
                        color: "text.disabled",
                        userSelect: "none",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? lineGutterBg[line.type].dark
                            : lineGutterBg[line.type].light,
                        borderRight: (theme) =>
                          `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {i + 1}
                    </Box>

                    {/* +/- prefix */}
                    <Box
                      sx={{
                        width: 20,
                        flexShrink: 0,
                        textAlign: "center",
                        fontSize: "11px",
                        fontFamily: codeFont,
                        fontWeight: 700,
                        lineHeight: "1.85em",
                        color: prefixColor[line.type],
                        userSelect: "none",
                      }}
                    >
                      {prefixChar[line.type]}
                    </Box>

                    {/* Code text */}
                    <Box
                      component="code"
                      sx={{
                        flex: 1,
                        fontSize: "11px",
                        fontFamily: codeFont,
                        lineHeight: "1.85em",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        pr: 1.5,
                        color:
                          line.type === "removed"
                            ? "#dc2626"
                            : line.type === "added"
                              ? "#059669"
                              : "text.primary",
                      }}
                    >
                      {line.text}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              /* Original or Suggested — single file view */
              <Box
                sx={{
                  flex: 1,
                  maxHeight: 340,
                  overflow: "auto",
                }}
              >
                {(viewMode === "original"
                  ? activeSnippet.original
                  : activeSnippet.suggested
                )
                  .split("\n")
                  .map((lineText, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        minHeight: "1.85em",
                        "&:hover": {
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "rgba(0,0,0,0.015)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          flexShrink: 0,
                          textAlign: "right",
                          pr: 1.2,
                          pl: 0.8,
                          fontSize: "9.5px",
                          fontFamily: codeFont,
                          lineHeight: "1.85em",
                          color: "text.disabled",
                          userSelect: "none",
                          borderRight: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#16161e"
                              : "#f6f6f7",
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Box
                        component="code"
                        sx={{
                          flex: 1,
                          pl: 1.5,
                          pr: 1.5,
                          fontSize: "11px",
                          fontFamily: codeFont,
                          lineHeight: "1.85em",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          color: "text.primary",
                        }}
                      >
                        {lineText}
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}

            {/* Status bar */}
            <Box
              sx={{
                px: 1.5,
                py: 0.4,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderTop: (theme) =>
                  `1px solid ${theme.palette.divider}`,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#16161e" : "#f3f3f3",
              }}
            >
              <Typography
                fontSize="9px"
                color="text.disabled"
                fontFamily={codeFont}
              >
                {activeSnippet.language}
              </Typography>
              <Typography fontSize="9px" color="text.disabled">
                {viewMode === "unified"
                  ? `${diffLines.length} ${t("lines")}`
                  : `${(viewMode === "original" ? activeSnippet.original : activeSnippet.suggested).split("\n").length} ${t("lines")}`}
              </Typography>
              <Box flex={1} />
              {viewMode === "unified" ? (
                <>
                  <Typography
                    fontSize="9px"
                    fontWeight={600}
                    color="#059669"
                  >
                    +{addedCount}
                  </Typography>
                  <Typography
                    fontSize="9px"
                    fontWeight={600}
                    color="#dc2626"
                  >
                    -{removedCount}
                  </Typography>
                </>
              ) : (
                <Typography
                  fontSize="9px"
                  fontWeight={600}
                  color={viewMode === "original" ? "#dc2626" : "#059669"}
                >
                  {viewMode === "original"
                    ? t("Original")
                    : t("Suggested")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Inline Affected Files ──────────────────────────────────────────────────

function InlineAffectedFiles({ files }: { files: AffectedFile[] }) {
  const { t } = useTranslation(["bugherd"]);
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: "8px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 1.5,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          cursor: "pointer",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "#fafafa",
          "&:hover": {
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "#f5f5f5",
          },
        }}
      >
        <FolderOpenIcon sx={{ fontSize: 13, color: "#d97706" }} />
        <Typography fontSize="11px" fontWeight={700}>
          {t("Affected Files")}
        </Typography>
        <Chip
          label={files.length}
          size="small"
          sx={{
            fontSize: "9px",
            height: 16,
            minWidth: 20,
            fontWeight: 700,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(217,119,6,0.12)"
                : "#fef3c7",
            color: "#d97706",
          }}
        />
        {/* Inline file names preview when collapsed */}
        {!expanded && (
          <Typography
            variant="caption"
            color="text.disabled"
            fontSize="10px"
            noWrap
            sx={{ flex: 1, minWidth: 0, ml: 0.5 }}
          >
            {files.map((f) => f.path.split("/").pop()).join(" · ")}
          </Typography>
        )}
        <Box flex={1} />
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: "text.secondary",
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1.2 }}>
          {files.map((file, idx) => (
            <Box
              key={idx}
              sx={{
                px: 1.2,
                py: 0.8,
                mb: idx < files.length - 1 ? 0.8 : 0,
                borderRadius: "6px",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                transition: "border-color 0.15s",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: 12, color: "primary.main" }}
                />
                <Typography
                  fontFamily="monospace"
                  fontSize="10.5px"
                  fontWeight={600}
                  color="primary.main"
                >
                  {file.path}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize="10.5px"
                sx={{ display: "block", lineHeight: 1.5, mb: 0.4 }}
              >
                {file.description}
              </Typography>
              {file.suggestedChanges && (
                <Box
                  display="flex"
                  alignItems="flex-start"
                  gap={0.5}
                  sx={{
                    mt: 0.3,
                    px: 1,
                    py: 0.5,
                    borderRadius: "4px",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(16,185,129,0.06)"
                        : "#f0fdf4",
                  }}
                >
                  <ArrowForwardIcon
                    sx={{ fontSize: 10, color: "#059669", mt: 0.15 }}
                  />
                  <Typography
                    variant="caption"
                    fontSize="9.5px"
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "#6ee7b7" : "#059669",
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {file.suggestedChanges}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Small Helpers ──────────────────────────────────────────────────────────

/** Tabbed Root Cause / Suggested Fix in a single compact card */
function InsightTabs({
  rootCause,
  suggestedFix,
  onCopy,
}: {
  rootCause: string;
  suggestedFix: string;
  onCopy: (text: string) => void;
}) {
  const { t } = useTranslation(["bugherd"]);
  const [activeTab, setActiveTab] = useState<"rootCause" | "suggestedFix">("rootCause");

  const tabs = [
    {
      key: "rootCause" as const,
      label: t("Root Cause"),
      icon: <LightbulbOutlinedIcon sx={{ fontSize: 13 }} />,
      color: "#d97706",
      accentLight: "#fffbeb",
      accentDark: "rgba(217,119,6,0.08)",
    },
    {
      key: "suggestedFix" as const,
      label: t("Suggested Fix"),
      icon: <AutoFixHighIcon sx={{ fontSize: 13 }} />,
      color: "#059669",
      accentLight: "#ecfdf5",
      accentDark: "rgba(16,185,129,0.08)",
    },
  ];

  const activeConfig = tabs.find((t) => t.key === activeTab)!;
  const activeText = activeTab === "rootCause" ? rootCause : suggestedFix;

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: "8px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      }}
    >
      {/* Tab bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#1a1a2a" : "#f9f9fa",
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                px: 1.5,
                py: 0.7,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                borderBottom: isActive
                  ? `2px solid ${tab.color}`
                  : "2px solid transparent",
                bgcolor: isActive
                  ? (theme) =>
                      theme.palette.mode === "dark"
                        ? tab.accentDark
                        : tab.accentLight
                  : "transparent",
                transition: "all 0.15s",
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? `${tab.color}10`
                      : `${tab.color}08`,
                },
              }}
            >
              <Box sx={{ color: isActive ? tab.color : "text.disabled", display: "flex" }}>
                {tab.icon}
              </Box>
              <Typography
                fontSize="10.5px"
                fontWeight={isActive ? 700 : 500}
                color={isActive ? tab.color : "text.secondary"}
              >
                {tab.label}
              </Typography>
            </Box>
          );
        })}
        <Box flex={1} />
        <CopyBtn
          onClick={(e) => {
            e.stopPropagation();
            onCopy(activeText);
          }}
        />
        <Box sx={{ width: 8 }} />
      </Box>

      {/* Content */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          maxHeight: 180,
          overflow: "auto",
          borderLeft: (theme) =>
            `3px solid ${theme.palette.mode === "dark" ? activeConfig.accentDark : activeConfig.color}20`,
        }}
      >
        {activeTab === "rootCause" ? (
          <Typography
            variant="body2"
            fontSize="12px"
            color="text.secondary"
            sx={{ lineHeight: 1.75 }}
          >
            {rootCause}
          </Typography>
        ) : (
          <SuggestedFixContent text={suggestedFix} />
        )}
      </Box>
    </Box>
  );
}

function DashboardStat({
  icon,
  value,
  label,
  color,
  highlight,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  color: string;
  highlight?: boolean;
}) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        flex: 1,
        minWidth: 120,
        px: 2,
        py: 1.5,
        borderRight: (theme) =>
          `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#e5e7eb"}`,
        "&:last-child": { borderRight: "none" },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? `${color}18` : `${color}14`,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          fontSize="15px"
          fontWeight={800}
          color={highlight ? "text.disabled" : "text.primary"}
          lineHeight={1.2}
        >
          {value}
        </Typography>
        <Typography fontSize="10px" fontWeight={600} color="text.secondary" lineHeight={1.3}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function MetaItem({
  icon,
  label,
  sub,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  sub?: string;
  mono?: boolean;
}) {
  return (
    <Box display="flex" alignItems="center" gap={0.4}>
      <Box sx={{ color: "text.disabled", display: "flex" }}>{icon}</Box>
      <Typography
        fontSize="10.5px"
        fontWeight={600}
        color="text.primary"
        sx={{ fontFamily: mono ? "monospace" : "inherit" }}
      >
        {label}
      </Typography>
      {sub && (
        <Typography fontSize="9.5px" color="text.disabled" fontWeight={500}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function CopyBtn({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <Tooltip title="Copy">
      <IconButton size="small" onClick={onClick} sx={{ p: 0.2 }}>
        <ContentCopyIcon sx={{ fontSize: 11, color: "text.disabled" }} />
      </IconButton>
    </Tooltip>
  );
}

function SuggestedFixContent({ text }: { text: string }) {
  if (text.includes("1)") && text.includes("2)")) {
    const steps = text
      .split(/\d+\)\s*/)
      .filter(Boolean)
      .map((s) => s.replace(/,\s*$/, "").trim());

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {steps.map((step, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            {/* Step number badge */}
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.1,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(16,185,129,0.12)"
                    : "#d1fae5",
                color: "#059669",
                fontSize: "10px",
                fontWeight: 800,
              }}
            >
              {i + 1}
            </Box>
            <Typography
              variant="body2"
              fontSize="12px"
              color="text.secondary"
              sx={{ lineHeight: 1.7 }}
            >
              {step}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Typography
      variant="body2"
      fontSize="12px"
      color="text.secondary"
      sx={{ lineHeight: 1.75 }}
    >
      {text}
    </Typography>
  );
}

export default BugAnalysis;
